import { useState, useCallback } from 'react';
import { useServices } from '@/di/precon-supporter';
import { useLearningProgress } from './useLearningProgress';
import type { PasserRequest as DomainPasserRequest } from '@/domain/precon-supporter/types/passer';

export type PasserRequest = {
  gender: string;
  birthyear: number;
  job: string;
  jobType?: string;
  prefecture: string;
  courseId: string;
};

// NOTE: API は成功時にレスポンスボディを参照しないため、
// このフックは成功時に boolean を返す設計にしています。

/**
 * Hook to create a passer by POSTing to /api/passers
 */
export function usePasser(courseId: 'basic' | 'advanced') {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { passerService } = useServices();
  const { progress, resetExamination } = useLearningProgress(courseId);

  const createPasser = useCallback(
    async (req: PasserRequest): Promise<boolean> => {
      if (!passerService) {
        setError('サービスが利用できません');
        return false;
      }

      // passerTokenを取得
      const passerToken = progress?.getPasserToken() ?? '';

      try {
        setLoading(true);
        setError(null);

        const domainReq: DomainPasserRequest = {
          gender: req.gender,
          birthyear: req.birthyear,
          job: req.job,
          jobType: req.jobType,
          prefecture: req.prefecture,
          courseId: req.courseId,
        };

        await passerService.createPasser(domainReq, passerToken);
        // 成功時はレスポンスを参照しないため true を返す
        return true;
      } catch (err) {
        // 認証期限切れの場合の特別な処理
        console.log('usePasser error:', err);
        if (err instanceof Error && err.message === 'AUTHORIZATION_EXPIRED') {
          // 試験合格状態をリセット
          try {
            await resetExamination();
          } catch (resetErr) {
            console.error('Failed to reset examination status:', resetErr);
          }
          setError('AUTHORIZATION_EXPIRED');
          return false;
        }

        setError(
          err instanceof Error ? err.message : '証明書の取得に失敗しました'
        ); // 属性登録の失敗とは表示しない
        return false;
      } finally {
        setLoading(false);
      }
    },
    [passerService, progress, resetExamination]
  );

  return { createPasser, loading, error } as const;
}
